import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, ArrowLeft, MailCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email }, { withCredentials: true });
      setSent(true);
      toast.success("If that email exists, a reset link is on its way.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="forgot-page">
      <Card className="w-full max-w-md bg-[#0a0a0b] border-white/10 text-white p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/prestigious-logo.png" alt="Prestigious" className="h-12 w-12" />
        </div>

        {sent ? (
          <div className="text-center py-6" data-testid="forgot-sent">
            <MailCheck className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold font-display mb-2">Check your inbox</h2>
            <p className="text-sm text-zinc-400 mb-6">
              If an account exists for <b>{email}</b>, we&apos;ve emailed a link that lets you set a new password.
              The link expires in 60 minutes.
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-orange-300 hover:text-orange-200" data-testid="forgot-back-login">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="overline mb-2">Password reset</div>
              <h2 className="text-2xl font-semibold font-display">Forgot your password?</h2>
              <p className="text-sm text-zinc-400 mt-1">Enter your email and we&apos;ll send a secure link to reset it.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 bg-white/[0.04] border-white/10 text-white"
                />
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-full h-11"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-zinc-400">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-orange-300 hover:text-orange-200" data-testid="forgot-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
